import React from 'react'
import { useNavigate } from 'react-router-dom'

import { Button } from '@factorialco/deprecated-design-system'
import {
  ExtraHoursRangeFragment,
  SettingPageEmployeesSettingFragment,
} from 'generated/resources/graphql'
import { isEqual, omit } from 'lodash'

import Modal from 'modules/core/components/Modal'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import ManageSettingVersionModal from 'modules/attendance/components/SettingItemPage/TimeTracking/ManageSettingVersionModal'
import { useDeleteExtraRuleRange } from 'modules/attendance/hooks/useDeleteExtraRuleRange'
import type AttendanceSetting from 'modules/attendance/store/models/AttendanceSetting'

interface Props {
  extraHourRule: ExtraHoursRangeFragment
  onClose: () => void
  attendanceSetting: AttendanceSetting
}

const DeleteExtraHourRuleModal = ({ extraHourRule, onClose, attendanceSetting }: Props) => {
  const [openSettingsVersionModal, setSettingsVersionModal] = React.useState(false)
  const { deleteHourRange, loading } = useDeleteExtraRuleRange()
  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const handleDeleteRange = async (payload: { id: number }) => {
    track(trackingEvents.timeTracking.customRuleDeletePressed)
    const result = await deleteHourRange(payload)
    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t(
          'time_tracking.settings.work_time_categories.extra_hours_rules.delete_modal.message'
        ),
      })
      onClose()
    }
  }

  const onCreateSetting = async (newSetting: SettingPageEmployeesSettingFragment) => {
    const newSettingId = +newSetting.id
    const { extraHoursRange } = newSetting ?? {}
    const extraHourRuleCopy = omit(extraHourRule, ['id', 'attendanceEmployeesSettingId'])

    const matchingRange = extraHoursRange.find((e) =>
      isEqual(extraHourRuleCopy, omit(e, ['id', 'attendanceEmployeesSettingId']))
    )

    const matchingRangeId = matchingRange?.id

    if (!matchingRangeId) return

    handleDeleteRange({ id: matchingRangeId })

    const routeParams = {
      id: 'time_tracking',
      settingId: newSettingId.toString(),
      tabId: 'work_time_categories',
    }
    navigate(navigation.settings.time_tracking.settingId.tabId.fullPath(routeParams))
  }

  const onUpdate = () => handleDeleteRange({ id: extraHourRule.id })

  return (
    <>
      {!openSettingsVersionModal && (
        <Modal
          title={i18n.t(
            'time_tracking.settings.work_time_categories.extra_hours_rules.delete_modal.title'
          )}
          description={i18n.t(
            'time_tracking.settings.work_time_categories.extra_hours_rules.delete_modal.description'
          )}
          illustration="alert"
          onClose={onClose}
          onSubmit={() => setSettingsVersionModal(true)}
          footer={
            <>
              <Button type="negative" disabled={loading} submit>
                {i18n.t('common.delete')}
              </Button>
              <Button type="positive" disabled={loading} onClick={onClose}>
                {i18n.t('common.keep')}
              </Button>
            </>
          }
        />
      )}
      {openSettingsVersionModal && (
        <ManageSettingVersionModal
          onCreate={onCreateSetting}
          onUpdate={onUpdate}
          onClose={onClose}
          running={loading}
          attendanceSettingId={attendanceSetting.attributes.id}
          attendanceSettingEffectiveAt={attendanceSetting.attributes.effective_at}
        />
      )}
    </>
  )
}

export default DeleteExtraHourRuleModal
